import DropDownButton from 'Components/_NewElements/DropDownButton';
import { fm } from 'Components/FormatMessage';
import { Item } from 'Components/Grid';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowListing from 'Components/HistoryRowListing';
import HistorySection from 'Components/HistorySection';
import { alzheimer } from 'neuro-schemas';
import * as React from 'react';
import { clinicallyEstablishedOrProbableOrPossible } from 'Routes/Diagnosis/utils';
import { parkinsonCriteriaTypes } from 'Routes/Diagnosis/utils/definitions';
import { formatPartialDate } from 'Utility/date';
import { filterDocumentsByType, sortDocumentsDescending } from 'Utility/documentHandling';
import FTDDiagnosticCriteriaHistory from './FTD';
import PPADiagnosticCriteriaHistory from './PPA';

import type { IHistoryContext } from 'Containers/FormContextHandler';
import type { TParkinsonCriteriaType } from 'Routes/Diagnosis/utils/definitions';

const {
  utils: { interpretAlzheimerDiagnosticCriteriaABCD },
} = alzheimer;

const getDefaultCriteriaResult = <Type extends SafeExtract<TDocumentType, 'pd' | 'msa' | 'dlb' | 'cbd'>>(
  d: IDocumentWithTypeV2<Type>,
  type: Type,
): React.ReactNode => {
  const result = clinicallyEstablishedOrProbableOrPossible(d);
  if (!result) return '-';
  return ['no', 'unknown'].includes(result) ? fm(`diagnosis.${result}`) : fm(`diagnosis.${type}.${result}`);
};

const DEFAULT_DOCUMENTS: IDocumentV2[] = [];
const ParkinsonDiagnosticsCriteria = ({
  documents = DEFAULT_DOCUMENTS,
  startEdit,
  view,
}: IOwnProps): React.JSX.Element => {
  const docs = filterDocumentsByType(documents, parkinsonCriteriaTypes).toSorted(sortDocumentsDescending);

  return (
    <HistorySection
      headerText={fm('diagnosis.parkinsonDiagnosticCriteria.title')}
      hasHistoryRows={false}
      newButton={
        <DropDownButton
          text="diagnosis.parkinsonDiagnosticCriteria.new"
          width={15}
          height={3}
          fontSize={14}
          buttons={parkinsonCriteriaTypes.map(type => ({
            text: `diagnosis.${type}.new`,
            onClick: e => {
              startEdit?.({}, type)(e);
            },
          }))}
          mainButtonOtherProps={{ alternate: true }}
          rounded={false}
        />
      }
    >
      <HistoryRowListing
        documents={docs}
        headers={
          <React.Fragment>
            <Item xs={2}>{fm('general.date')}</Item>
            <Item xs={3}>{fm('diagnosis.parkinsonDiagnosticCriteria.criteria')}</Item>
            <Item xs={4}>{fm('diagnosis.parkinsonDiagnosticCriteria.resultSummary')}</Item>
            <Item xs={3} />
          </React.Fragment>
        }
        contentFormat={(d: (typeof docs)[0]): React.JSX.Element => {
          const type = d._type;
          return (
            <React.Fragment>
              <Item xs={2} style={{ fontWeight: '600' }}>
                {formatPartialDate(d.date)}
              </Item>
              <Item xs={3}>{fm(`diagnosis.${type}.title`)}</Item>
              <Item xs={4}>
                {(() => {
                  switch (d._type) {
                    case 'psp':
                      return <></>;
                    case 'ftd':
                      return <FTDDiagnosticCriteriaHistory d={d} />;
                    case 'ppa':
                      return <PPADiagnosticCriteriaHistory d={d} />;
                    case 'alzheimer': {
                      if (interpretAlzheimerDiagnosticCriteriaABCD(d)) {
                        return fm(`diagnosis.${type}.interpretation.${interpretAlzheimerDiagnosticCriteriaABCD(d)}`);
                      }
                      return '-';
                    }
                    case 'cbd':
                    case 'dlb':
                    case 'msa':
                    case 'pd':
                      return getDefaultCriteriaResult(d, d._type);
                  }
                })()}
              </Item>
              <Item xs={3}>
                {startEdit ? <HistoryRowControls document={d} startEdit={startEdit} view={view} /> : null}
              </Item>
            </React.Fragment>
          );
        }}
      />
    </HistorySection>
  );
};

// oxlint-disable-next-line typescript/no-empty-interface typescript/no-empty-object-type
interface IOwnProps extends IHistoryContext<TParkinsonCriteriaType> {}

export default ParkinsonDiagnosticsCriteria;
